/**
 * PWABootstrap handles service worker registration and lifecycle management.
 * This module runs BEFORE AngularJS bootstraps, ensuring the service worker
 * is ready before any Angular services depend on it.
 *
 * Error Handling:
 *   - If SW is unsupported, resolves with { supported: false }
 *   - If registration fails, resolves with { supported: false, error: Error }
 *   - Never rejects - app can always continue without SW
 *   - All errors are reported to Sentry when available
 *
 * Usage:
 *   import PWABootstrap from './ib/pwa/PWABootstrap';
 *   PWABootstrap.init();  // Call immediately on load
 *   const result = await PWABootstrap.ready();
 *   if (result.supported) { ... }  // SW is controlling the page
 */

// Timeout for waiting on service worker events (30 seconds)
const SW_EVENT_TIMEOUT_MS = 30000;
// Timeout for update check in dev mode (2 seconds)
// 2s is typically enough for browser to compare SW scripts locally;
// we don't want to block app startup indefinitely waiting for slow network
const UPDATE_CHECK_TIMEOUT_MS = 2000;
// How long to show the "refreshing" message before reloading (ms)
const RELOAD_MESSAGE_DELAY_MS = 3000;

let readyPromise = null;
let readyResolve = null;
let registration = null;

/**
 * Report an error to Sentry if available
 * @param {Error} error - The error to report
 * @param {string} operation - Description of the operation that failed
 * @param {Object} extra - Additional context to include
 */
function reportError(error, operation, extra = {}) {
    console.error(`PWABootstrap: ${operation}:`, error);
    if (window.Sentry) {
        window.Sentry.captureException(error, {
            tags: { component: 'PWABootstrap', operation },
            extra: {
                userAgent: navigator.userAgent,
                url: window.location.href,
                ...extra
            }
        });
    }
}

/**
 * Update the bootstrap loading UI status text
 * @param {string} message - Status message to display
 */
function updateStatus(message) {
    const statusEl = document.getElementById('pwa-bootstrap-status');
    if (statusEl) {
        statusEl.textContent = message;
    }
}

/**
 * Hide the bootstrap loading UI
 */
function hideLoadingUI() {
    const loadingEl = document.getElementById('pwa-bootstrap-loading');
    if (loadingEl) {
        loadingEl.style.opacity = '0';
        loadingEl.style.transition = 'opacity 0.3s';
        setTimeout(() => loadingEl.remove(), 300);
    }
}

/**
 * Reload the page after showing a message to the user.
 * Uses a soft refresh to avoid potential infinite loops from unregistering SW.
 * @param {string} reason - Reason for the refresh (shown to user)
 */
async function reloadPage(reason) {
    console.log('PWABootstrap: Initiating page reload:', reason);
    updateStatus(`${reason} - Refreshing page...`);

    // Report to Sentry before refresh
    if (window.Sentry) {
        window.Sentry.captureMessage('PWABootstrap: Page reload initiated', {
            level: 'warning',
            tags: { component: 'PWABootstrap', operation: 'reloadPage' },
            extra: { reason, userAgent: navigator.userAgent, url: window.location.href }
        });
    }

    // Wait so user can read the message
    await new Promise(resolve => setTimeout(resolve, RELOAD_MESSAGE_DELAY_MS));

    // Soft refresh - let the browser/SW sort it out
    location.reload();
}

/**
 * Request persistent storage to prevent IndexedDB eviction under storage pressure.
 *
 * Browsers may automatically evict IndexedDB data when disk space gets tight.
 * This is especially relevant on ChromeOS-based school Chromebooks with limited
 * storage that is shared across multiple student profiles.
 */
function requestPersistentStorage() {
    if (!navigator.storage || typeof navigator.storage.persist !== 'function') {
        console.log('PWABootstrap: Persistent storage API not available');
        return;
    }

    navigator.storage.persist().then((granted) => {
        console.log('PWABootstrap: Persistent storage granted =', granted);
        if (!granted && window.Sentry) {
            window.Sentry.captureMessage('PWABootstrap: Persistent storage denied', {
                level: 'warning',
                tags: { component: 'PWABootstrap', operation: 'requestPersistentStorage' },
                extra: { userAgent: navigator.userAgent }
            });
        }
    }).catch((error) => {
        reportError(error, 'requestPersistentStorage');
    });
}

/**
 * Initialize service worker registration. Call this early in app startup.
 * @returns {Promise<{supported: boolean, registration?: ServiceWorkerRegistration, error?: Error}>}
 *          Resolves with status object. If supported is false, no SW is controlling.
 */
function init() {
    if (readyPromise) {
        return readyPromise;
    }

    // Fire-and-forget: independent of SW registration, runs in parallel
    requestPersistentStorage();

    readyPromise = new Promise((resolve) => {
        readyResolve = resolve;
        registerServiceWorker();
    });

    return readyPromise;
}

/**
 * Returns a promise that resolves when service worker initialization completes.
 * Check the returned object's `supported` property to determine if SW is actually ready.
 * Safe to call multiple times - returns same promise.
 * @returns {Promise<{supported: boolean, registration?: ServiceWorkerRegistration, error?: Error}>}
 */
function ready() {
    if (!readyPromise) {
        return init();
    }
    return readyPromise;
}

/**
 * Get the current service worker registration.
 * @returns {ServiceWorkerRegistration|null} The registration, or null if not yet registered or unsupported
 */
function getRegistration() {
    return registration;
}

async function registerServiceWorker() {
    console.log('PWABootstrap: Starting service worker registration');
    updateStatus('Initializing...');

    if (!('serviceWorker' in navigator)) {
        console.log('PWABootstrap: Service workers not supported');
        hideLoadingUI();
        readyResolve({ supported: false });
        return;
    }

    try {
        // Fetch manifest to get SW path
        updateStatus('Loading configuration...');
        const response = await fetch('/sw-asset-manifest2.json');
        if (!response.ok) {
            throw new Error(`Failed to load service worker manifest: HTTP ${response.status} ${response.statusText}`);
        }

        let manifest;
        try {
            manifest = await response.json();
        } catch (parseError) {
            throw new Error(`Failed to parse service worker manifest: ${parseError.message}`);
        }

        const swPathRaw = manifest['serviceWorker.js'];
        if (!swPathRaw) {
            throw new Error('Service worker path not found in manifest (serviceWorker.js key missing)');
        }
        const swPath = swPathRaw.replace(/^auto\//, '');

        // Register service worker (handles dev vs production mode internally)
        updateStatus('Registering service worker...');
        registration = await registerWithMode(swPath);

        if (!registration) {
            throw new Error('Service worker registration returned null');
        }

        console.log('PWABootstrap: Service worker registered, scope:', registration.scope);

        // Wait for SW to be ready and controlling
        updateStatus('Starting service worker...');
        await waitForControl();

        console.log('PWABootstrap: Service worker is controlling the page');
        updateStatus('Ready');
        hideLoadingUI();
        readyResolve({ supported: true, registration });

    } catch (error) {
        reportError(error, 'registration');

        // Check if this is a timeout error - if so, refresh the page
        const isTimeoutError = error.message && error.message.includes('Timeout waiting for');
        if (isTimeoutError) {
            // Don't resolve - reloadPage will reload the page
            await reloadPage('Service worker timed out');
            return;
        }

        // For other errors, show message and continue without SW
        updateStatus('Error: ' + error.message);
        // Keep error visible for 5 seconds so user can see it
        setTimeout(hideLoadingUI, 5000);
        readyResolve({ supported: false, error });
    }
}

async function registerWithMode(swPath) {
    // Detect dev vs production mode by checking for Webpack content hash
    // Production builds include hash: serviceWorker.abc123def.js
    // Dev builds have no hash: serviceWorker.js
    const hasContentHash = /serviceWorker\.[a-f0-9]+\.js$/.test(swPath);
    const absoluteSwUrl = new URL(swPath, window.location.origin).href;

    if (!hasContentHash) {
        // Dev mode: always register and force update check
        // This ensures code changes are detected on refresh
        console.log('PWABootstrap: Dev mode detected, forcing registration');
        const reg = await navigator.serviceWorker.register(swPath, { updateViaCache: 'none' });

        // Set up listener for updatefound BEFORE calling update()
        // updatefound fires when browser finds a new service worker to install
        const updateFoundPromise = new Promise((resolve) => {
            reg.addEventListener('updatefound', () => {
                console.log('PWABootstrap: Update found, new worker installing');
                resolve(true);
            }, { once: true });
        });

        // Timeout promise - if no update found within timeout, proceed
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(() => {
                console.log('PWABootstrap: No update found within timeout');
                resolve(false);
            }, UPDATE_CHECK_TIMEOUT_MS);
        });

        // Trigger the update check
        console.log('PWABootstrap: Checking for updates...');
        reg.update().catch(err => {
            // Log and report update check failures, but don't block startup
            reportError(err, 'updateCheck', { swPath, mode: 'dev' });
        });

        // Wait for either update found or timeout
        await Promise.race([updateFoundPromise, timeoutPromise]);

        return reg;
    }

    // Production mode: use smart registration (hash change = different URL)
    const controller = navigator.serviceWorker.controller;

    if (controller && controller.scriptURL === absoluteSwUrl) {
        console.log('PWABootstrap: SW already controlling');
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) {
            throw new Error('Controller exists but no registration found');
        }
        return reg;
    }

    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
        const activeUrl = reg.active?.scriptURL;
        const installingUrl = reg.installing?.scriptURL;
        const waitingUrl = reg.waiting?.scriptURL;
        if (activeUrl === absoluteSwUrl || installingUrl === absoluteSwUrl || waitingUrl === absoluteSwUrl) {
            console.log('PWABootstrap: Reusing existing registration');
            return reg;
        }
    }

    console.log('PWABootstrap: Registering new service worker');
    return navigator.serviceWorker.register(swPath);
}

/**
 * Create a promise that races against a timeout
 * @param {Promise} promise - The promise to race
 * @param {string} operation - Description of the operation (for error messages)
 * @returns {Promise} - Resolves when promise resolves, rejects on timeout
 */
function withTimeout(promise, operation) {
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            setTimeout(() => {
                const error = new Error(`Timeout waiting for ${operation} after ${SW_EVENT_TIMEOUT_MS}ms`);
                reportError(error, 'timeout', { operation });
                reject(error);
            }, SW_EVENT_TIMEOUT_MS);
        })
    ]);
}

async function waitForControl() {
    // If there's an installing worker, wait for it to finish installing first
    if (registration?.installing) {
        console.log('PWABootstrap: Waiting for installing worker to finish');
        await withTimeout(
            new Promise((resolve, reject) => {
                registration.installing.addEventListener('statechange', (e) => {
                    console.log('PWABootstrap: Installing worker state changed to:', e.target.state);
                    if (e.target.state === 'installed' || e.target.state === 'activated') {
                        resolve();
                    } else if (e.target.state === 'redundant') {
                        const error = new Error('Service worker installation failed (became redundant)');
                        reportError(error, 'installationFailed');
                        reject(error);
                    }
                });
            }),
            'service worker installation'
        );
    }

    // If there's a waiting worker, tell it to skip waiting and take control
    // BUT ONLY if there's no exam in progress - we don't want to replace the SW mid-exam
    if (registration?.waiting) {
        const examInProgress = sessionStorage.getItem('examInProgress') === 'true';

        console.log('PWABootstrap: Waiting worker detected -', examInProgress ? 'exam in progress' : 'no exam',
            '- waiting:', registration.waiting.scriptURL,
            '- controller:', navigator.serviceWorker.controller?.scriptURL || 'none');

        if (examInProgress) {
            console.log('PWABootstrap: ========== DEFERRING SW UPDATE (exam in progress) ==========');
            sessionStorage.setItem('deferredSwUpdate', 'true');
            return;
        }

        console.log('PWABootstrap: No exam in progress, sending SKIP_WAITING');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        await withTimeout(
            new Promise((resolve) => {
                navigator.serviceWorker.addEventListener('controllerchange', () => {
                    console.log('PWABootstrap: Controller changed after SKIP_WAITING');
                    resolve();
                }, { once: true });
            }),
            'controller change after SKIP_WAITING'
        );
        return;
    }

    // If we already have a controller, we're done
    if (navigator.serviceWorker.controller) {
        console.log('PWABootstrap: Already have controller');
        return;
    }

    // No controller yet - wait for one (first install scenario)
    console.log('PWABootstrap: Waiting for service worker to take control');
    await withTimeout(
        new Promise((resolve) => {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                console.log('PWABootstrap: Controller changed (first install)');
                resolve();
            }, { once: true });
        }),
        'initial controller activation'
    );
}

export default {
    init,
    ready,
    getRegistration
};
